// The session view's model (docs/brand.md, "the session as an orbit"): the timeline's events
// classified into signals, the ephemeris (the notable ones, numbered), the periapsis (the moment
// the session came closest to trouble) and the orbit's geometry.
import type { SessionSummary, SessionTimeline } from '../api/types'
import { ATTR, EVENT, OUTCOME, SEVERITY_ERROR, SPAN } from './conventions'

/** RFC 3339 with up to nanoseconds, to epoch milliseconds (Date parses only milliseconds). */
export function parseTime(value: string): number {
  const m = /^(.*T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})$/.exec(value)
  if (!m) return new Date(value).getTime()
  const frac = (m[2] ?? '').padEnd(3, '0').slice(0, 3)
  return new Date(`${m[1]}.${frac}${m[3]}`).getTime()
}

export type Signal =
  | 'start'
  | 'screen'
  | 'network'
  | 'span'
  | 'crash'
  | 'hang'
  | 'exit'
  | 'error'
  | 'breadcrumb'
  | 'lifecycle'
  | 'log'

export type Tone = 'crash' | 'warn' | 'plain'

export interface TimelineEvent {
  id: string
  signal: Signal
  /** Milliseconds since the session started. */
  at: number
  /** Milliseconds; 0 for an instant. */
  duration: number
  /** A telemetry name or a request line: never translated. */
  name: string
  sub: string
  /** The value column: a duration, a status code, or a signal word (translated by the view). */
  value: string
  valueKind: 'duration' | 'status' | 'signal'
  tone: Tone
  attributes: Record<string, string>
  /** The record's trace, empty when it has none: the event's trace link (APS-315). */
  traceId: string
  /** A hang that began before the app first became active (spec 6.3, APS-531). */
  phase?: 'launch'
}

const HTTP_FAIL = 400

function classify(
  e: SessionTimeline['events'][number],
  t0: number,
  position: number,
): Omit<TimelineEvent, 'traceId'> | undefined {
  const at = parseTime(e.ts) - t0
  const attrs = e.attributes
  // Record ids are unique per table, not across logs and spans (and an app may reuse span ids).
  const id = `${e.type}:${e.id}:${position}`
  if (e.type === 'span') {
    const duration = e.duration_ns / 1e6
    const base = { id, at, duration, attributes: attrs }
    const request = httpName(attrs, e.name)
    if (request) {
      const status = Number(attrs[ATTR.httpStatus] ?? 0)
      const failed = status >= HTTP_FAIL || e.status_code === 'ERROR'
      return {
        ...base,
        signal: 'network',
        name: request,
        sub: status ? String(status) : '',
        value: status ? String(status) : '',
        valueKind: status ? 'status' : 'duration',
        tone: failed ? 'crash' : 'plain',
      }
    }
    if (e.name === SPAN.start)
      return {
        ...base,
        signal: 'start',
        name: e.name,
        sub: attrs[ATTR.startType] ?? '',
        value: '',
        valueKind: 'duration',
        tone: 'plain',
      }
    if (e.name === SPAN.screenLoad)
      return {
        ...base,
        signal: 'screen',
        name: e.name,
        sub: attrs[ATTR.screenName] ?? '',
        value: '',
        valueKind: 'duration',
        tone: 'plain',
      }
    return {
      ...base,
      signal: 'span',
      name: e.name,
      sub: '',
      value: '',
      valueKind: 'duration',
      tone: e.status_code === 'ERROR' ? 'crash' : 'plain',
    }
  }
  const instant = { id, at, duration: 0, attributes: attrs }
  switch (e.event_name) {
    case EVENT.sessionSummary:
      return undefined
    case EVENT.crash:
      return {
        ...instant,
        signal: 'crash',
        name: e.event_name,
        sub: attrs[ATTR.exceptionType] ?? '',
        value: 'crash',
        valueKind: 'signal',
        tone: 'crash',
      }
    case EVENT.hang: {
      const ms = Number(attrs[ATTR.hangDuration] ?? 0)
      return {
        ...instant,
        duration: ms,
        signal: 'hang',
        name: 'hang',
        sub: 'main thread',
        value: '',
        valueKind: 'duration',
        tone: 'warn',
        ...(attrs[ATTR.hangPhase] === 'launch' ? { phase: 'launch' as const } : {}),
      }
    }
    case EVENT.abnormalExit:
      return {
        ...instant,
        signal: 'exit',
        name: e.event_name,
        sub: e.body,
        value: 'exit',
        valueKind: 'signal',
        tone: 'warn',
      }
    case EVENT.breadcrumb:
      return {
        ...instant,
        signal: 'breadcrumb',
        name: attrs[ATTR.breadcrumbCategory] ?? 'breadcrumb',
        sub: e.body,
        value: '',
        valueKind: 'signal',
        tone: 'plain',
      }
    case EVENT.sessionStart:
      return {
        ...instant,
        signal: 'lifecycle',
        name: e.event_name,
        sub: '',
        value: '',
        valueKind: 'signal',
        tone: 'plain',
      }
  }
  const name = e.event_name || attrs[ATTR.exceptionType] || e.severity_text || 'error'
  if (e.severity_number >= SEVERITY_ERROR)
    return {
      ...instant,
      signal: 'error',
      name,
      // Its message, else its type, but never the name again ("DecodingError · DecodingError").
      sub:
        [attrs[ATTR.exceptionMessage], e.body, attrs[ATTR.exceptionType]].find(
          (x) => x && x !== name,
        ) ?? '',
      value: 'error',
      valueKind: 'signal',
      tone: 'crash',
    }
  return {
    ...instant,
    signal: 'log',
    name: e.event_name || 'log',
    sub: e.body,
    value: e.severity_text.toLowerCase(),
    valueKind: 'signal',
    tone: 'plain',
  }
}

/**
 * A request's name, as the events table and the span tree both say it: its method and route
 * (http.route, else url.template, the path or the full URL's path). Not a request: undefined.
 */
export function httpName(attrs: Record<string, string>, spanName: string): string | undefined {
  const method = attrs[ATTR.httpMethod]
  if (!method) return undefined
  const target =
    attrs[ATTR.httpRoute] ??
    attrs[ATTR.urlTemplate] ??
    attrs[ATTR.urlPath] ??
    pathOf(attrs[ATTR.urlFull]) ??
    (spanName === method ? undefined : spanName)
  return target ? `${method} ${target}` : method
}

function pathOf(url: string | undefined): string | undefined {
  if (!url) return undefined
  try {
    return new URL(url).pathname
  } catch {
    return url
  }
}

const SEVERITY: Record<Signal, number> = {
  crash: 6,
  exit: 5,
  hang: 4,
  error: 3,
  network: 1,
  start: 1,
  screen: 1,
  span: 0,
  breadcrumb: 0,
  lifecycle: 0,
  log: 0,
}

const weight = (e: TimelineEvent) => SEVERITY[e.signal] + (e.tone === 'crash' ? 2 : 0)

export interface SessionModel {
  summary: SessionSummary | null
  startedAt: number
  /** Milliseconds from start to the last thing recorded. */
  length: number
  events: TimelineEvent[]
  /** The numbered events on the orbit and in the ephemeris, in time order. */
  ephemeris: TimelineEvent[]
  /** Index into `ephemeris` of the periapsis. */
  periapsis: number
  screens: number
  requests: number
  failedRequests: number
  outcome: string
}

export const EPHEMERIS_MAX = 12

export function sessionModel(timeline: SessionTimeline): SessionModel {
  const summary = timeline.summary
  const firstTs = timeline.events[0] ? parseTime(timeline.events[0].ts) : Date.now()
  const startedAt = summary ? Math.min(parseTime(summary.started_at), firstTs) : firstTs
  const events = timeline.events
    .map((e, i) => {
      const c = classify(e, startedAt, i)
      return c ? { ...c, traceId: e.trace_id ?? '' } : undefined
    })
    .filter((e): e is TimelineEvent => e !== undefined)
    .sort((a, b) => a.at - b.at)
  const end = events.reduce((m, e) => Math.max(m, e.at + e.duration), 0)

  // The ephemeris: everything notable first, then the rest by time, at most EPHEMERIS_MAX.
  const notable = events.filter((e) => e.signal !== 'log' && e.signal !== 'lifecycle')
  const pool = notable.length > 0 ? notable : events
  const keep = new Set(
    [...pool]
      .map((e, i) => ({ e, i }))
      .sort((a, b) => weight(b.e) - weight(a.e) || a.i - b.i)
      .slice(0, EPHEMERIS_MAX)
      .map(({ e }) => e),
  )
  const ephemeris = pool.filter((e) => keep.has(e))

  // The periapsis: the worst moment (the latest of equals), or the last event when nothing
  // went wrong.
  let periapsis = ephemeris.length - 1
  let worst = 1
  ephemeris.forEach((e, i) => {
    if (weight(e) >= worst && weight(e) > 1) {
      worst = weight(e)
      periapsis = i
    }
  })

  const requests = events.filter((e) => e.signal === 'network')
  return {
    summary,
    startedAt,
    length: end,
    events,
    ephemeris,
    periapsis,
    screens: events.filter((e) => e.signal === 'screen').length,
    requests: requests.length,
    failedRequests: requests.filter((e) => e.tone === 'crash').length,
    outcome: summary?.outcome ?? (events.some((e) => e.signal === 'crash') ? OUTCOME.crashed : ''),
  }
}

// The orbit (docs/brand.md): an ellipse around the device at its right focus; time runs along
// the upper half from apoapsis (left, the start) to periapsis (right), linear in angle.
export const ORBIT = { cx: 290, cy: 150, rx: 240, ry: 110 } as const
/** The focus, where the device is drawn: c = sqrt(rx² − ry²) to the right of the center. */
export const FOCUS = {
  x: ORBIT.cx + Math.sqrt(ORBIT.rx ** 2 - ORBIT.ry ** 2),
  y: ORBIT.cy,
} as const

export interface Point {
  x: number
  y: number
}

/** A point on the recorded half: fraction 0 is the start, 1 the periapsis. */
export function orbitPoint(fraction: number): Point {
  const f = Math.min(1, Math.max(0, fraction))
  const theta = Math.PI * (1 - f)
  return {
    x: round(ORBIT.cx + ORBIT.rx * Math.cos(theta)),
    y: round(ORBIT.cy - ORBIT.ry * Math.sin(theta)),
  }
}

const round = (n: number) => Math.round(n * 100) / 100

/** An SVG arc along the recorded half between two fractions. */
export function orbitArc(from: number, to: number): string {
  const a = orbitPoint(from)
  const b = orbitPoint(to)
  return `M ${a.x} ${a.y} A ${ORBIT.rx} ${ORBIT.ry} 0 0 1 ${b.x} ${b.y}`
}

/**
 * Each ephemeris event's fraction along the orbit, linear in time from the start to the last
 * numbered event. A session that ends in a crash has its periapsis at the right end.
 */
export function orbitFractions(model: SessionModel): number[] {
  const last = model.ephemeris.at(-1)
  const span = last ? last.at : 0
  return model.ephemeris.map((e) => (span > 0 ? Math.min(1, Math.max(0, e.at / span)) : 1))
}
